import { type FormEvent, useEffect, useState } from 'react';
import { readError, request } from './api';
import { SchemaView } from '../modules/analytics/SchemaView';
import { DataQualityView } from '../modules/analytics/DataQualityView';
import { AnalysisWorkspace } from '../modules/analytics/AnalysisWorkspace';
import { AnalyticsFilterBar } from '../modules/analytics/AnalyticsFilterBar';
import {
  createAnalyticsFilterQuery,
  readInitialAnalyticsFilterScope,
  writeAnalyticsFilterScopeToLocation,
  type AnalyticsFilterScope,
} from '../modules/analytics/AnalyticsFilterScope';
import { ANALYTICS_PAGE_SIZE } from '../modules/analytics/analytics-api';
import { EventExplorerView } from '../modules/analytics/EventExplorerView';
import { InstallationExplorerView } from '../modules/analytics/InstallationExplorerView';
import { OverviewView } from '../modules/analytics/OverviewView';
import { SessionCountry } from '../modules/analytics/GeoUi';
import { PaginatedSessionEventTimeline } from '../modules/analytics/PaginatedSessionEventTimeline';
import { PaginationControls } from '../modules/analytics/PaginationControls';
import { PlatformBadge } from '../modules/analytics/PlatformUi';
import type { SessionPage, SessionSummary } from '../modules/analytics/types';
import { UsersView } from '../modules/users/UsersView';
import { hasAdminPermission, type AdminUser } from '../modules/users/types';
import { formatDateTime } from '../shared/date-time';
import { useRelativeTimeRefresh } from '../shared/use-relative-time-refresh';

type View = 'overview' | 'installs' | 'sessions' | 'events' | 'analysis' | 'quality' | 'schema' | 'users';

interface Environment {
  id: string;
  slug: string;
  displayName: string;
  type: string;
}

interface Project {
  id: string;
  slug: string;
  displayName: string;
  environments: Environment[];
}

export function App(): React.JSX.Element {
  useRelativeTimeRefresh();
  const [user, setUser] = useState<AdminUser | null>(null);
  const [csrfToken, setCsrfToken] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [environmentId, setEnvironmentId] = useState('');
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [analyticsFilters, setAnalyticsFilters] = useState<AnalyticsFilterScope>(readInitialAnalyticsFilterScope);
  const [view, setView] = useState<View>(readViewFromLocation);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    void restoreSession();
  }, []);

  useEffect(() => {
    function restoreLocation(): void {
      setView(readViewFromLocation());
      setAnalyticsFilters(readInitialAnalyticsFilterScope());
      const requestedEnvironmentId = readEnvironmentFromLocation();
      if (requestedEnvironmentId !== null) setEnvironmentId(requestedEnvironmentId);
    }
    window.addEventListener('popstate', restoreLocation);
    return () => { window.removeEventListener('popstate', restoreLocation); };
  }, []);

  useEffect(() => {
    writeAnalyticsFilterScopeToLocation(analyticsFilters);
  }, [analyticsFilters]);

  async function restoreSession(): Promise<void> {
    try {
      const me = await request<{ user: AdminUser }>('/admin/v1/auth/me');
      const csrf = await request<{ csrfToken: string }>('/admin/v1/auth/csrf', { method: 'POST' });
      setUser(me.user);
      setCsrfToken(csrf.csrfToken);
      await loadContext();
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  async function login(email: string, password: string): Promise<void> {
    setError('');
    setLoading(true);
    try {
      const response = await request<{ user: AdminUser; csrfToken: string }>('/admin/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      setUser(response.user);
      setCsrfToken(response.csrfToken);
      await loadContext();
    } catch (caught) {
      setError(readError(caught));
    } finally {
      setLoading(false);
    }
  }

  async function logout(): Promise<void> {
    try {
      await request('/admin/v1/auth/logout', { method: 'POST', headers: { 'X-CSRF-Token': csrfToken } });
    } finally {
      setUser(null);
      setProjects([]);
    }
  }

  async function loadContext(): Promise<void> {
    const context = await request<{ projects: Project[] }>('/admin/v1/platform/context');
    setProjects(context.projects);
    const environments = context.projects.flatMap(readProjectEnvironments);
    const requestedEnvironmentId = readEnvironmentFromLocation();
    const selectedEnvironment = environments.find((environment) => environment.id === requestedEnvironmentId) ?? environments[0];
    if (selectedEnvironment !== undefined) {
      setEnvironmentId(selectedEnvironment.id);
      writeDashboardLocation(view, selectedEnvironment.id, 'replace');
    }
  }

  function openSession(sessionId: string): void {
    setSelectedSession(sessionId);
    navigateView('sessions');
  }

  if (loading && user === null) {
    return <LoadingScreen />;
  }
  if (user === null) {
    return <LoginScreen error={error} loading={loading} onLogin={login} />;
  }

  const activeProject = projects.find((project) => project.environments.some(hasEnvironmentId));
  const activeEnvironment = activeProject?.environments.find(isActiveEnvironment);
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand"><span>V</span><strong>Voogzy</strong></div>
        <nav>
          <button className={view === 'overview' ? 'nav-item active' : 'nav-item'} onClick={showOverview}>Overview</button>
          <button className={view === 'installs' ? 'nav-item active' : 'nav-item'} onClick={showInstalls}>Installs</button>
          <button className={view === 'sessions' ? 'nav-item active' : 'nav-item'} onClick={showSessions}>Sessions</button>
          <button className={view === 'events' ? 'nav-item active' : 'nav-item'} onClick={showEvents}>Events</button>
          <button className={view === 'analysis' ? 'nav-item active' : 'nav-item'} onClick={showAnalysis}>Analysis</button>
          <button className={view === 'quality' ? 'nav-item active' : 'nav-item'} onClick={showQuality}>Data quality</button>
          <button className={view === 'schema' ? 'nav-item active' : 'nav-item'} onClick={showSchema}>Schema</button>
          <span className="nav-label">Platform</span>
          <button className={view === 'users' ? 'nav-item active' : 'nav-item'} onClick={showUsers}>Users</button>
          <button className="nav-item" disabled>Remote Config <small>later</small></button>
          <button className="nav-item" disabled>Experiments <small>later</small></button>
        </nav>
        <div className="user-block">
          <span>{initials(user.displayName)}</span>
          <div><strong>{user.displayName}</strong><small>{user.email}</small></div>
          <button onClick={logout} title="Sign out">↗</button>
        </div>
      </aside>
      <main className="content">
        <header className="content-header">
          <div><span className="eyebrow">{view === 'users' ? 'Platform' : 'Analytics'}</span><h1>{readViewTitle(view)}</h1></div>
          {view !== 'users' && <label className="environment-picker">
            <span>{activeProject?.displayName ?? 'Project'}</span>
            <select value={environmentId} onChange={changeEnvironment}>
              {projects.flatMap(renderEnvironmentOptions)}
            </select>
          </label>}
        </header>
        {error !== '' && <div className="error-banner">{error}</div>}
        <ViewContent
          view={view}
          environmentId={environmentId}
          environment={activeEnvironment}
          analyticsFilters={analyticsFilters}
          selectedSession={selectedSession}
          csrfToken={csrfToken}
          user={user}
          onChangeAnalyticsFilters={changeAnalyticsFilters}
          onOpenSession={openSession}
        />
      </main>
    </div>
  );

  function hasEnvironmentId(environment: Environment): boolean {
    return environment.id === environmentId;
  }

  function isActiveEnvironment(environment: Environment): boolean {
    return environment.id === environmentId;
  }

  function renderEnvironmentOptions(project: Project): React.JSX.Element[] {
    return project.environments.map((environment) => (
      <option key={environment.id} value={environment.id}>{project.displayName} · {environment.displayName}</option>
    ));
  }

  function changeEnvironment(event: React.ChangeEvent<HTMLSelectElement>): void {
    const nextEnvironmentId = event.target.value;
    setEnvironmentId(nextEnvironmentId);
    setSelectedSession(null);
    setAnalyticsFilters((current) => ({ ...current, appVersions: [], countries: [] }));
    writeDashboardLocation(view, nextEnvironmentId, 'push');
  }

  function showOverview(): void {
    navigateView('overview');
  }

  function showInstalls(): void {
    navigateView('installs');
  }

  function showSessions(): void {
    navigateView('sessions');
  }

  function clearSession(): void {
    setSelectedSession(null);
  }

  function changeAnalyticsFilters(scope: AnalyticsFilterScope): void {
    setAnalyticsFilters(scope);
    clearSession();
  }

  function showEvents(): void { navigateView('events'); }
  function showAnalysis(): void { navigateView('analysis'); }
  function showQuality(): void { navigateView('quality'); }

  function showSchema(): void {
    navigateView('schema');
  }

  function showUsers(): void {
    navigateView('users');
  }

  function navigateView(nextView: View): void {
    setView(nextView);
    writeDashboardLocation(nextView, environmentId, 'push');
  }
}

function ViewContent(props: {
  view: View;
  environmentId: string;
  environment: Environment | undefined;
  analyticsFilters: AnalyticsFilterScope;
  selectedSession: string | null;
  csrfToken: string;
  user: AdminUser;
  onChangeAnalyticsFilters: (scope: AnalyticsFilterScope) => void;
  onOpenSession: (id: string) => void;
}): React.JSX.Element {
  switch (props.view) {
    case 'overview': return <OverviewView environmentId={props.environmentId} filters={props.analyticsFilters} onChangeFilters={props.onChangeAnalyticsFilters} onOpenSession={props.onOpenSession} />;
    case 'installs': return <InstallationExplorerView environmentId={props.environmentId} filters={props.analyticsFilters} onChangeFilters={props.onChangeAnalyticsFilters} />;
    case 'sessions': return <SessionsView environmentId={props.environmentId} environment={props.environment} filters={props.analyticsFilters} selectedSession={props.selectedSession} onChangeFilters={props.onChangeAnalyticsFilters} onOpenSession={props.onOpenSession} />;
    case 'events': return <EventExplorerView environmentId={props.environmentId} filters={props.analyticsFilters} onChangeFilters={props.onChangeAnalyticsFilters} />;
    case 'analysis': return <AnalysisWorkspace environmentId={props.environmentId} />;
    case 'quality': return <DataQualityView environmentId={props.environmentId} />;
    case 'schema': return <SchemaView environmentId={props.environmentId} csrfToken={props.csrfToken} canManage={hasAdminPermission(props.user, 'analytics.schema.manage')} />;
    case 'users': return <UsersView user={props.user} csrfToken={props.csrfToken} />;
  }
}

function LoginScreen(props: { error: string; loading: boolean; onLogin: (email: string, password: string) => Promise<void> }): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  function submit(event: FormEvent): void {
    event.preventDefault();
    void props.onLogin(email, password);
  }

  return (
    <main className="login-shell">
      <section className="login-card">
        <div className="brand login-brand"><span>V</span><strong>Voogzy Platform</strong></div>
        <span className="eyebrow">Private control room</span>
        <h1>See the colony<br />as players build it.</h1>
        <p>Sign in to inspect development analytics for every configured game and environment.</p>
        <form onSubmit={submit}>
          <label>Email<input type="email" autoComplete="username" value={email} onChange={changeEmail} required /></label>
          <label>Password<input type="password" autoComplete="current-password" value={password} onChange={changePassword} required /></label>
          {props.error !== '' && <div className="form-error">{props.error}</div>}
          <button className="primary-button" disabled={props.loading}>{props.loading ? 'Signing in…' : 'Sign in'}</button>
        </form>
      </section>
      <aside className="login-art"><div className="orb one" /><div className="orb two" /><span>Analytics · Remote Config · Experiments</span></aside>
    </main>
  );

  function changeEmail(event: React.ChangeEvent<HTMLInputElement>): void {
    setEmail(event.target.value);
  }

  function changePassword(event: React.ChangeEvent<HTMLInputElement>): void {
    setPassword(event.target.value);
  }
}

function LoadingScreen(): React.JSX.Element {
  return <main className="loading-screen"><div className="brand"><span>V</span><strong>Voogzy</strong></div><p>Opening the control room…</p></main>;
}

function SessionsView(props: {
  environmentId: string;
  environment: Environment | undefined;
  filters: AnalyticsFilterScope;
  selectedSession: string | null;
  onChangeFilters: (filters: AnalyticsFilterScope) => void;
  onOpenSession: (id: string) => void;
}): React.JSX.Element {
  const [result, setResult] = useState<SessionPage | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setPage(1);
  }, [props.environmentId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const query = createAnalyticsFilterQuery(props.environmentId, props.filters);
    query.set('page', String(page));
    query.set('pageSize', String(ANALYTICS_PAGE_SIZE));
    request<SessionPage>(`/admin/v1/reports/sessions?${query.toString()}`)
      .then((response) => {
        if (!active) return;
        setResult(response);
        if (response.pagination.page !== page) setPage(response.pagination.page);
        setError('');
      })
      .catch((caught: unknown) => { if (active) setError(readError(caught)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [page, props.environmentId, props.filters]);

  const sessions = result?.sessions ?? [];
  const selectedSession = sessions.find((session) => session.sessionId === props.selectedSession);

  return (
    <section className="analytics-page">
      <AnalyticsFilterBar environmentId={props.environmentId} scope={props.filters} onChange={changeFilters} />
      {error !== '' && <div className="error-banner">{error}</div>}
      <section className="session-layout">
        <article className="surface session-list">
          <div className="surface-heading"><div><span className="eyebrow">{props.environment?.displayName}</span><h2>Recent sessions</h2></div><strong>{formatNumber(result?.pagination.totalItems ?? 0)}</strong></div>
          {sessions.map((session) => <SessionButton key={session.sessionId} session={session} active={session.sessionId === props.selectedSession} onOpen={props.onOpenSession} />)}
          {sessions.length === 0 && loading && <EmptyState text="Loading sessions…" compact />}
          {sessions.length === 0 && !loading && <EmptyState text="No sessions match these filters." compact />}
          {result !== null && <PaginationControls pagination={result.pagination} disabled={loading} onPageChange={setPage} />}
        </article>
        <article className="surface event-stream">
          <div className="surface-heading"><div><span className="eyebrow">Timeline</span><h2>{props.selectedSession === null ? 'Choose a session' : shortId(props.selectedSession)}</h2></div><strong>{selectedSession === undefined ? '—' : `${formatNumber(selectedSession.eventCount)} events`}</strong></div>
          {selectedSession !== undefined && <div className="session-network-context"><SessionCountry countryCode={selectedSession.countryCode} countryCodes={selectedSession.countryCodes} />{selectedSession.sourceIps !== undefined && <span className="sensitive-network-detail">Source IPs <code>{selectedSession.sourceIps.join(', ') || '—'}</code></span>}</div>}
          {props.selectedSession === null ? <EmptyState text="Select a session to inspect its events in order." /> : <PaginatedSessionEventTimeline key={props.selectedSession} environmentId={props.environmentId} sessionId={props.selectedSession} />}
        </article>
      </section>
    </section>
  );

  function changeFilters(filters: AnalyticsFilterScope): void {
    setPage(1);
    props.onChangeFilters(filters);
  }
}

function SessionButton(props: { session: SessionSummary; active: boolean; onOpen: (id: string) => void }): React.JSX.Element {
  return <button className={props.active ? 'session-button active' : 'session-button'} onClick={() => props.onOpen(props.session.sessionId)}><span><strong>{shortId(props.session.sessionId)}</strong><small>{formatDate(props.session.startedAt)}</small><SessionCountry countryCode={props.session.countryCode} countryCodes={props.session.countryCodes} /><PlatformBadge code={props.session.platformCode} /></span><span><strong>{props.session.eventCount}</strong><small>{formatDuration(props.session.durationMs)}</small></span></button>;
}

function EmptyState(props: { text: string; compact?: boolean }): React.JSX.Element {
  return <div className={props.compact === true ? 'empty compact' : 'empty'}>{props.text}</div>;
}

function initials(name: string): string {
  return name.split(' ').slice(0, 2).map((part) => part[0]).join('').toUpperCase();
}

function shortId(value: string): string {
  return value.slice(0, 8);
}

function formatDuration(milliseconds: number): string {
  const seconds = Math.round(milliseconds / 1000);
  return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

function formatDate(value: string): string {
  return formatDateTime(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat().format(value);
}

function readViewTitle(view: View): string {
  switch (view) {
    case 'overview':
      return 'Colony pulse';
    case 'installs':
      return 'Install explorer';
    case 'sessions':
      return 'Session explorer';
    case 'events':
      return 'Event explorer';
    case 'analysis':
      return 'Gameplay analysis';
    case 'quality':
      return 'Data quality';
    case 'schema':
      return 'Schema registry';
    case 'users':
      return 'Users & access';
  }
}

function readProjectEnvironments(project: Project): Environment[] {
  return project.environments;
}

function readViewFromLocation(): View {
  const requested = new URLSearchParams(window.location.search).get('view');
  return isView(requested) ? requested : 'overview';
}

function readEnvironmentFromLocation(): string | null {
  return new URLSearchParams(window.location.search).get('environment');
}

function writeDashboardLocation(view: View, environmentId: string, method: 'push' | 'replace'): void {
  const url = new URL(window.location.href);
  url.searchParams.set('view', view);
  if (environmentId !== '') url.searchParams.set('environment', environmentId);
  if (method === 'push') {
    window.history.pushState({}, '', url);
  } else {
    window.history.replaceState({}, '', url);
  }
}

function isView(value: string | null): value is View {
  return value === 'overview'
    || value === 'installs'
    || value === 'sessions'
    || value === 'events'
    || value === 'analysis'
    || value === 'quality'
    || value === 'schema'
    || value === 'users';
}
